import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  Company,
  companyAssociatedActivityDomains,
  CompanyUploadedDocumentsKeys,
  CompanyWall,
  CreateCompanyActivityDomain,
  Department,
  Office,
  StoreState,
  UpdateCompanyActivityDomain,
  User,
} from '@job-v2/ats-utils';
import { Store } from '../../commons/store';
import { catchError, firstValueFrom, Observable } from 'rxjs';
import { errorHandle } from '../../commons/errorHandling';

const DEFAULT_STATE: StoreState<Company> = {
  loaded: false,
  loading: false,
  error: false,
  totalRecords: 0,
  currentPage: 0,
  data: [],
};

@Injectable({
  providedIn: 'root',
})
export class CompaniesService extends Store<StoreState<Company>> {
  constructor(private httpClient: HttpClient) {
    super(httpClient, DEFAULT_STATE);
  }

  currentState!: StoreState<Company>;
  private apiUrl = `${environment.apiUrl}/companies`;

  getJobCompanies () {
    const url = `${this.apiUrl}/list/full-details`;
    return this.httpClient.get<Company[]>(url);
  }

  uploadFile(
    companyAlias: string,
    key: string,
    file: File,
  ): Observable<{ fileName: string; key: string; url: string }[]> {
    const formData = new FormData();
    formData.append('files', file);
    const url = `${this.apiUrl}/upload/documents/${key}/${companyAlias}`;
    return this.httpClient.post<{ fileName: string; key: string; url: string }[]>(
      url,
      formData,
    );
  }

  getCompanyOldestUser(companyId: string): Observable<User> {
    const url = `${this.apiUrl}/${companyId}/get-oldest-user`;
    return this.httpClient.get<User>(url);
  }

  getCompanyActivityDomains(
    companyId: string,
  ): Observable<companyAssociatedActivityDomains> {
    const url = `${this.apiUrl}/activities-domains/${companyId}/all`;
    return this.httpClient.get<companyAssociatedActivityDomains>(url);
  }

  updateWallDetails(companyId: string, wallDetails: CompanyWall) {
    const url = `${this.apiUrl}/wall/${companyId}`;
    return this.httpClient.put<CompanyWall>(url, wallDetails);
  }

  getWallDetails(companyId: string): Observable<CompanyWall> {
    const url = `${this.apiUrl}/wall/${companyId}`;
    return this.httpClient.get<CompanyWall>(url)
        .pipe(catchError((error) => errorHandle(error)));
  }

  getDepartements(companyId: string): Observable<Department[]> {
    const url = `${this.apiUrl}/${companyId}/departments`;
    return this.httpClient.get<Department[]>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }

  getCompanyRecruiters(id: string): Observable<User[]> {
    const url = `${this.apiUrl}/${id}/recruiters`;
    return this.httpClient.get<User[]>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }

  createOffice(body: Partial<Office>): Observable<Office> {
    const url = `${environment.workSpaceEndpoint}/sites`;
    return this.httpClient.post<Office>(url, body)
    .pipe(catchError((error) => errorHandle(error)));
  }

  async doesOfficeRelatedToSomeUsers(id: string): Promise<boolean> {
    return await firstValueFrom(
      this.httpClient.get<boolean>(
        `${environment.workSpaceEndpoint}/sites/${id}/has-users`,
      ),
    );
  }

  deleteOffice(id: string): Observable<Office> {
    const url = `${environment.workSpaceEndpoint}/sites/${id}`;
    return this.httpClient.delete<Office>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }

  getOffices(companyId: string): Observable<Office[]> {
    const url = `${environment.workSpaceEndpoint}/sites/${companyId}`;
    return this.httpClient.get<Office[]>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }

  deleteFile(fileUrl: string): Observable<boolean | null> {
    const url = `${this.apiUrl}/delete/documents`;
    return this.httpClient.post<boolean | null>(url, {
      url: fileUrl,
    })
    .pipe(catchError((error) => errorHandle(error)));
  }

  getCompanyCGU(
    companyAlias: string,
    key: CompanyUploadedDocumentsKeys,
    docName: string,
  ): Observable<{ data: string }> {
    const url = `${this.apiUrl}/documents/${key}/${companyAlias}/${docName}`;
    return this.httpClient.get<{ data: string }>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }

  updateCompanyActivityDomain(
    body: UpdateCompanyActivityDomain,
  ): Observable<companyAssociatedActivityDomains> {
    const url = `${this.apiUrl}/activities-domains/${body.id}`;
    return this.httpClient.put<companyAssociatedActivityDomains>(url, body)
    .pipe(catchError((error) => errorHandle(error)));
  }

  createCompanyActivityDomain(
    body: CreateCompanyActivityDomain,
  ): Observable<CreateCompanyActivityDomain> {
    const url = `${this.apiUrl}/activities-domains`;
    return this.httpClient.post<CreateCompanyActivityDomain>(url, body)
    .pipe(catchError((error) => errorHandle(error)));
  }

  updateSiteHeadOffice(
    body: Partial<Office>,
    siteId: string,
  ): Observable<Office> {
    const url = `${environment.workSpaceEndpoint}/sites/${siteId}`;
    return this.httpClient.patch<Office>(url, body)
    .pipe(catchError((error) => errorHandle(error)));
  }

  bulkUpdateJobs(
    filters: Record<string, unknown>,
    updates: Record<string, unknown>,
  ): Observable<{ modifiedCount: number }> {
    const url = `${environment.apiUrl}/recruitment/jobs/bulk-update`;
    return this.httpClient.patch<{ modifiedCount: number }>(url, { filters, updates })
      .pipe(catchError((error) => errorHandle(error)));
  }

  updateCompany(body: Partial<Company>, id: string): Observable<Company> {
    const url = `${environment.workSpaceEndpoint}/companies/${id}`;
    return this.httpClient.patch<Company>(url, body)
    .pipe(catchError((error) => errorHandle(error)));
  }


  async getCompanyTree (companyId: string) {
    const url = `${environment.workSpaceEndpoint}/companies/${companyId}/tree`;
    return this.httpClient.get<Company>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }

  async getCompanyFullTree (companyId: string) {
    const url = `${environment.workSpaceEndpoint}/companies/${companyId}/full-tree`;
    return this.httpClient.get<Company>(url)
    .pipe(catchError((error) => errorHandle(error)));
  }
}
